/**
 * Declarative entry rules a recipe can attach to a parameter's `config` (PROJECT_SPEC.md §6.1 "Data-entry
 * validation: type, range, format masks, plausibility cross-checks, forbidden-value lists, duplicate-entry
 * detection", URS-MBR-021). They run inside `validateEntry`, so the keypad, a pasted value, a scanner, the
 * offline client and the API are all judged by the same code (LIM-018).
 *
 *   pattern        TEXT: regular expression the WHOLE entry must match (format mask, e.g. a lot-number shape)
 *   plausibleMin / plausibleMax
 *                  numeric: typo guard INSIDE the specification. 1000 kg on a 100 kg scale is a slip of the finger,
 *                  not an out-of-spec result; it is refused and must be re-keyed, never recorded as data
 *   crossCheck     { op: "<"|"<="|">"|">=", ref: "OTHER_PARAM" }: relation to a value already recorded in the batch
 *   unique         true: the same value may not be recorded twice in the batch (container / label / seal numbers)
 */
import { dec } from "../calc/decimal.js";

export interface EntryContext {
  /** Current value of each parameter recorded so far in the batch, by parameter code. */
  recorded?: Readonly<Record<string, string>>;
  /** Current values already recorded for THIS parameter anywhere in the batch (duplicate detection). */
  prior?: readonly string[];
}
export interface EntryRuleRejection {
  code: "FORMAT_MISMATCH" | "IMPLAUSIBLE" | "CROSS_CHECK_FAILED" | "DUPLICATE_ENTRY";
  message: string;
}

const OPS = {
  "<": (a: number, b: number) => a < b,
  "<=": (a: number, b: number) => a <= b,
  ">": (a: number, b: number) => a > b,
  ">=": (a: number, b: number) => a >= b,
} as const;
type Op = keyof typeof OPS;

function formatRejection(label: string, pattern: string, value: string): EntryRuleRejection | null {
  try {
    if (new RegExp(`^(?:${pattern})$`).test(value)) return null;
  } catch {
    // a malformed mask is a recipe defect: refuse (fail closed) rather than accept unchecked text
  }
  return { code: "FORMAT_MISMATCH", message: `${label} does not match the required format.` };
}

function plausibilityRejection(
  label: string,
  cfg: Record<string, unknown>,
  value: string,
): EntryRuleRejection | null {
  const lo = cfg["plausibleMin"];
  const hi = cfg["plausibleMax"];
  const v = dec(value);
  const low = typeof lo === "string" && v.lt(dec(lo));
  const high = typeof hi === "string" && v.gt(dec(hi));
  return low || high
    ? {
        code: "IMPLAUSIBLE",
        message: `${label}: ${value} is outside the plausible range; check the entry and re-key it.`,
      }
    : null;
}

function crossRejection(
  label: string,
  cfg: Record<string, unknown>,
  value: string,
  ctx: EntryContext,
): EntryRuleRejection | null {
  const rule = cfg["crossCheck"] as { op?: string; ref?: string } | undefined;
  if (!rule?.ref || !rule.op || !(rule.op in OPS)) return null;
  const other = ctx.recorded?.[rule.ref];
  if (other === undefined) return null; // not recorded yet: nothing to compare with
  if (OPS[rule.op as Op](Number(dec(value)), Number(dec(other)))) return null;
  return {
    code: "CROSS_CHECK_FAILED",
    message: `${label} must be ${rule.op} ${rule.ref} (${other}); received ${value}.`,
  };
}

/** First rule the entry breaks, or null. Numeric rules apply only to a string already known to be a decimal. */
export function entryRuleRejection(
  label: string,
  dataType: string,
  cfg: Record<string, unknown>,
  value: string,
  ctx: EntryContext = {},
  isDecimal = /^-?\d+(\.\d+)?$/.test(value.trim()),
): EntryRuleRejection | null {
  const v = value.trim();
  if (dataType === "TEXT" && typeof cfg["pattern"] === "string") {
    const r = formatRejection(label, cfg["pattern"], v);
    if (r) return r;
  }
  if ((dataType === "DECIMAL" || dataType === "INTEGER") && isDecimal) {
    const r = plausibilityRejection(label, cfg, v) ?? crossRejection(label, cfg, v, ctx);
    if (r) return r;
  }
  const same = (p: string): boolean =>
    isDecimal && /^-?\d+(\.\d+)?$/.test(p.trim())
      ? dec(p.trim()).eq(dec(v))
      : p.trim().toLowerCase() === v.toLowerCase();
  if (cfg["unique"] === true && ctx.prior?.some(same))
    return { code: "DUPLICATE_ENTRY", message: `${label}: “${v}” has already been recorded on this batch.` };
  return null;
}
